import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch } from "../../../../state/store";
import {
  setIsMissingKeysModalActive,
  setIsMissingKeysModalLocalizationUploaded,
  setIsNeedRefetchLocales,
  setLocalizationToApp,
  setMissingKeysModalData,
  setMissingKeysModalLocalization
} from "../../../../state/ducks";

import { languages, strCode } from "../../../../consts";
import { appsService, log, t } from "../../../../services";
import { localisationTabUtils } from "../utils/localisationTabUtils";
import { useIsLocalisationEditing } from "../hooks/useIsLocalisationEditing";

import { DropdownInput, Tooltip, WrapperContainer } from "../../../../components";
import { LangTitle, MissingKeysCount } from "../styled";

export const LanguageCell = ({
  cell,
  getEditingLocalization,
  getOriginalDefaultLocalization,
  getLocalizationToRefetch,
  setLocalizationToRefetch
}) => {
  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const prevLocale = useSelector((store) => store.apps.prevLocale);
  const isNeedRefetchLocales = useSelector((store) => store.apps.isNeedRefetchLocales);

  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isLocalizationFetched, setIsLocalizationFetched] = useState(false);
  const [missingKeys, setMissingKeys] = useState({});

  const initialCellValue = useRef(cell.row.original);
  const missingKeysLabel = useRef(null);

  const missingKeysCount = useMemo(() => {
    return Object.keys(missingKeys).length;
  }, [missingKeys]);

  const localizations = useMemo(() => selectedApp.localizations, [selectedApp]);

  const defaultLocalization = useMemo(
    () => localizations.find((loc) => loc.isDefault),
    [localizations]
  );
  const lang = useMemo(() => languages.find((l) => l.id === cell.value), [cell]);
  const currentLocalization = useMemo(
    () => localizations.find((loc) => loc.language === lang?.id),
    [localizations, lang]
  );

  const languageOptions = useMemo(
    () => localisationTabUtils.formatLanguageOptions(languages),
    [languages]
  );

  const { isLocalizationEditing } = useIsLocalisationEditing({
    originalRow: cell.row.original,
    getEditingLocalization
  });

  useEffect(() => {
    const originalDefaultLocalization = getOriginalDefaultLocalization();

    if (
      (prevLocale && prevLocale.name !== originalDefaultLocalization?.name) ||
      isNeedRefetchLocales
    ) {
      setTimeout(() => {
        setIsLocalizationFetched(false);
      }, 3000);
    }
  }, [prevLocale, getOriginalDefaultLocalization, isNeedRefetchLocales]);

  useEffect(() => {
    const localizationToRefetch = getLocalizationToRefetch();

    if (
      localizationToRefetch?.name === currentLocalization?.name &&
      localizationToRefetch?.path === currentLocalization?.path
    ) {
      setTimeout(() => {
        setIsLocalizationFetched(false);
      }, 3000);
    }
  }, [getLocalizationToRefetch]);

  useEffect(() => {
    if (!getEditingLocalization()) {
      initialCellValue.current = cell.row.original;
    }
  }, [getEditingLocalization]);

  useEffect(async () => {
    if (
      lang?.id &&
      !isLocalizationFetched &&
      currentLocalization.name &&
      currentLocalization.path
    ) {
      try {
        const response = await appsService.getLocalization(currentLocalization.path);

        const newMissingKeys = localisationTabUtils.calculateMissingKeys(
          Object.keys(selectedApp.localizationKeys),
          Object.keys(response.data)
        );

        setMissingKeys(newMissingKeys);
        setIsLocalizationFetched(true);
        setLocalizationToRefetch(null);
      } catch (error) {
        log.error("Error fetching localization", error);
      } finally {
        if (isNeedRefetchLocales) {
          dispatch(setIsNeedRefetchLocales(false));
        }
      }
    }
  }, [lang?.id, currentLocalization, isLocalizationFetched, selectedApp.localizationKeys]);

  useEffect(() => {
    if (lang?.id) {
      setSelectedLanguage(languageOptions.find((opt) => opt.id === lang?.id));
    }
  }, [lang, languageOptions]);

  const isLocalizationKeysMissing = useMemo(() => {
    return missingKeysCount > 0;
  }, [defaultLocalization, lang, missingKeysCount]);

  const handleLanguageSelection = (id) => {
    const newLocalization = localizations.find((loc) => loc.name === cell.row.original.name);

    const newLocalizations = [...localizations].map((localization) => {
      if (localization.name === newLocalization.name) {
        return {
          ...localization,
          language: id,
          isDefault: localizations.length === 1 || newLocalization.isDefault
        };
      }

      return localization;
    });

    dispatch(setLocalizationToApp(newLocalizations));

    setSelectedLanguage(languageOptions.find((opt) => opt.id === id));
  };

  const onOpenMissingKeysModal = () => {
    dispatch(setMissingKeysModalData(Object.keys(missingKeys)));
    dispatch(setIsMissingKeysModalActive(true));
    dispatch(setMissingKeysModalLocalization(cell.row.original));
    dispatch(setIsMissingKeysModalLocalizationUploaded(true));
  };

  return (
    <>
      {!isLocalizationEditing ? (
        <LangTitle>{`${lang?.id} - ${lang?.value}`}</LangTitle>
      ) : (
        <WrapperContainer width={"100%"} mr={20}>
          <DropdownInput
            id="app-localization-language-select"
            options={languageOptions}
            selectedOption={selectedLanguage}
            onSelectOption={handleLanguageSelection}
            placeholder={t(strCode.SELECT_LANGUAGE_PLACEHOLDER)}
            isSearchable
          />
        </WrapperContainer>
      )}

      {isLocalizationKeysMissing && (
        <>
          <MissingKeysCount ref={missingKeysLabel} onClick={onOpenMissingKeysModal}>
            {missingKeysCount}
          </MissingKeysCount>

          <Tooltip
            childrenRef={missingKeysLabel}
            tooltipLabel={t(strCode.MISSING_KEYS_COUNT, {
              count: missingKeysCount
            })}
          />
        </>
      )}
    </>
  );
};

LanguageCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string.isRequired,
    row: PropTypes.shape({
      original: PropTypes.shape({
        name: PropTypes.string.isRequired
      }).isRequired
    }).isRequired
  }).isRequired,

  getEditingLocalization: PropTypes.func.isRequired,
  getOriginalDefaultLocalization: PropTypes.func.isRequired,
  getLocalizationToRefetch: PropTypes.func.isRequired,
  setLocalizationToRefetch: PropTypes.func.isRequired
};
